// Main App component

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import MapView from './components/map/MapView';
import PodDetails from './components/panels/PodDetails';
import OutletList from './components/panels/OutletList';
import ProximityStats from './components/panels/ProximityStats';
import OutletProximityChart from './components/charts/OutletProximityChart';
import CoverageDonut from './components/charts/CoverageDonut';
import { LoadingPage } from './components/ui/LoadingSpinner';
import usePods from './hooks/usePods';
import { searchFastFoodOutlets, isGooglePlacesConfigured, clearCache } from './services/googlePlaces';
import { FAST_FOOD_BRANDS } from './utils/constants';

// LocalStorage keys
const ALL_OUTLETS_KEY = 'frontseat_all_outlets_v2';
const OUTLETS_CACHE_VERSION = '2.0';
const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// Haversine distance calculation
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

function App() {
  // PODS data
  const { pods, loading: podsLoading, error: podsError, stats } = usePods();

  // All outlets state
  const [allOutlets, setAllOutlets] = useState([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');
  const hasLoadedRef = useRef(false);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedRadius, setSelectedRadius] = useState(2000);
  const [selectedBrands, setSelectedBrands] = useState([
    'Steers',
    'Debonairs',
    'Wimpy',
    'Fishaways',
  ]);
  const [selectedPod, setSelectedPod] = useState(null);
  const [viewMode, setViewMode] = useState('map');
  const [searchQuery, setSearchQuery] = useState('');

  // Get filtered PODS (only by province, not by search)
  const filteredPods = useMemo(() => {
    let result = pods;
    if (selectedProvince) {
      result = result.filter((pod) => pod.province === selectedProvince);
    }
    return result;
  }, [pods, selectedProvince]);

  // Load outlets - smart approach
  useEffect(() => {
    if (pods.length === 0 || hasLoadedRef.current) return;

    const loadOutlets = async () => {
      hasLoadedRef.current = true;

      // Check localStorage cache first
      try {
        const cached = localStorage.getItem(ALL_OUTLETS_KEY);
        if (cached) {
          const data = JSON.parse(cached);
          if (data.version === OUTLETS_CACHE_VERSION && data.outlets?.length > 0) {
            const age = Date.now() - data.timestamp;
            if (age < CACHE_DURATION) {
              console.log(`[Outlets] Loaded ${data.outlets.length} from cache (${Math.round(age / 60000)} min old)`);
              setAllOutlets(data.outlets);
              return;
            }
          }
        }
      } catch (e) {
        console.warn('[Outlets] Cache read error:', e);
      }

      // No valid cache - check if API configured
      if (!isGooglePlacesConfigured()) {
        console.warn('[Outlets] API not configured');
        return;
      }

      setOutletsLoading(true);
      setLoadProgress('Loading outlets...');

      try {
        // Strategy: Load in batches by province, parallel within batch
        const BATCH_SIZE = 3; // 3 parallel requests per batch
        const SEARCH_RADIUS = 5000; // 5km per location

        // Group PODs by approximate location (round to 0.03 degrees ~3km)
        const locationMap = new Map();
        pods.forEach(pod => {
          if (!pod.latitude || !pod.longitude) return;
          const key = `${Math.round(pod.latitude * 30) / 30}_${Math.round(pod.longitude * 30) / 30}`;
          if (!locationMap.has(key)) {
            locationMap.set(key, { lat: pod.latitude, lng: pod.longitude });
          }
        });

        const locations = Array.from(locationMap.values());
        console.log(`[Outlets] ${locations.length} unique locations to search`);

        const allFoundOutlets = [];
        const seenPlaceIds = new Set();

        // Process in parallel batches
        for (let i = 0; i < locations.length; i += BATCH_SIZE) {
          const batch = locations.slice(i, Math.min(i + BATCH_SIZE, locations.length));

          setLoadProgress(`Loading outlets... ${Math.min(i + BATCH_SIZE, locations.length)}/${locations.length}`);

          // Parallel requests within batch
          const batchResults = await Promise.all(
            batch.map(loc =>
              searchFastFoodOutlets({ lat: loc.lat, lng: loc.lng }, SEARCH_RADIUS, FAST_FOOD_BRANDS)
                .catch(err => {
                  console.warn('[Outlets] Batch error:', err);
                  return [];
                })
            )
          );

          // Merge results
          batchResults.forEach(outlets => {
            outlets.forEach(outlet => {
              if (!seenPlaceIds.has(outlet.placeId)) {
                seenPlaceIds.add(outlet.placeId);
                allFoundOutlets.push(outlet);
              }
            });
          });

          // Small delay between batches to avoid rate limiting
          if (i + BATCH_SIZE < locations.length) {
            await new Promise(r => setTimeout(r, 100));
          }
        }

        console.log(`[Outlets] Found ${allFoundOutlets.length} unique outlets`);

        // Save to cache
        if (allFoundOutlets.length > 0) {
          try {
            localStorage.setItem(ALL_OUTLETS_KEY, JSON.stringify({
              version: OUTLETS_CACHE_VERSION,
              timestamp: Date.now(),
              outlets: allFoundOutlets,
            }));
            console.log('[Outlets] Saved to cache');
          } catch (e) {
            console.warn('[Outlets] Cache save error:', e);
          }
        }

        setAllOutlets(allFoundOutlets);
      } catch (error) {
        console.error('[Outlets] Load error:', error);
      } finally {
        setOutletsLoading(false);
        setLoadProgress('');
      }
    };

    loadOutlets();
  }, [pods]);

  // Parse search query to extract brand and location
  const parsedSearch = useMemo(() => {
    if (!searchQuery.trim()) return { brand: null, location: null };

    const query = searchQuery.toLowerCase().trim();
    const parts = query.split(/[,\s]+/).filter(Boolean);

    // Check if any part matches a brand name
    let matchedBrand = null;
    let remainingParts = [...parts];

    for (const brand of FAST_FOOD_BRANDS) {
      const brandNameLower = brand.name.toLowerCase();
      const keywordLower = brand.keyword.toLowerCase();

      // Check for full brand name match or keyword match
      const fullQueryLower = query.toLowerCase();

      if (fullQueryLower.includes(brandNameLower) || fullQueryLower.includes(keywordLower)) {
        matchedBrand = brand.name;
        // Remove brand-related parts from remaining
        remainingParts = parts.filter(part =>
          !brandNameLower.includes(part) &&
          !keywordLower.includes(part) &&
          part !== brandNameLower &&
          part !== keywordLower
        );
        break;
      }
    }

    const location = remainingParts.join(' ').trim() || null;

    return { brand: matchedBrand, location };
  }, [searchQuery]);

  // Filter outlets client-side
  const filteredOutlets = useMemo(() => {
    let result = allOutlets;

    // Search-based brand filter (takes priority)
    if (parsedSearch.brand) {
      result = result.filter(outlet =>
        outlet.brand?.toLowerCase() === parsedSearch.brand.toLowerCase()
      );
    }
    // Sidebar brand filter (only if no search brand)
    else if (selectedBrands.length > 0 && selectedBrands.length < FAST_FOOD_BRANDS.length) {
      result = result.filter(outlet => selectedBrands.includes(outlet.brand));
    }

    // Search-based location filter for outlets (vicinity contains address info)
    if (parsedSearch.location) {
      const locationTerms = parsedSearch.location.split(/\s+/).filter(Boolean);
      result = result.filter(outlet => {
        const vicinity = outlet.vicinity?.toLowerCase() || '';
        const name = outlet.name?.toLowerCase() || '';
        // Match if ANY location term is found in vicinity or name
        return locationTerms.some(term =>
          vicinity.includes(term) || name.includes(term)
        );
      });
    }

    // If no POD selected, return filtered without distance
    if (!selectedPod) {
      return result.map(o => ({ ...o, distance: null }));
    }

    // Calculate distances and filter by radius
    return result
      .map(outlet => ({
        ...outlet,
        distance: calculateDistance(
          selectedPod.latitude, selectedPod.longitude,
          outlet.latitude, outlet.longitude
        ),
      }))
      .filter(outlet => outlet.distance <= selectedRadius)
      .sort((a, b) => a.distance - b.distance);
  }, [allOutlets, selectedPod, selectedRadius, selectedBrands, parsedSearch]);

  // Map outlets (all visible, with distances)
  const mapOutlets = useMemo(() => {
    let result = allOutlets;

    // Search-based brand filter (takes priority)
    if (parsedSearch.brand) {
      result = result.filter(outlet =>
        outlet.brand?.toLowerCase() === parsedSearch.brand.toLowerCase()
      );
    }
    // Sidebar brand filter (only if no search brand)
    else if (selectedBrands.length > 0 && selectedBrands.length < FAST_FOOD_BRANDS.length) {
      result = result.filter(outlet => selectedBrands.includes(outlet.brand));
    }

    // Search-based location filter for outlets
    if (parsedSearch.location) {
      const locationTerms = parsedSearch.location.split(/\s+/).filter(Boolean);
      result = result.filter(outlet => {
        const vicinity = outlet.vicinity?.toLowerCase() || '';
        const name = outlet.name?.toLowerCase() || '';
        // Match if ANY location term is found in vicinity or name
        return locationTerms.some(term =>
          vicinity.includes(term) || name.includes(term)
        );
      });
    }

    if (!selectedPod) return result;

    return result.map(outlet => ({
      ...outlet,
      distance: calculateDistance(
        selectedPod.latitude, selectedPod.longitude,
        outlet.latitude, outlet.longitude
      ),
    }));
  }, [allOutlets, selectedPod, selectedBrands, parsedSearch]);

  // Outlets that match the search (for map centering)
  const searchedOutlets = useMemo(() => {
    if (!parsedSearch.brand && !parsedSearch.location) return [];
    return mapOutlets;
  }, [mapOutlets, parsedSearch]);

  // Handlers
  const handleProvinceChange = useCallback((province) => {
    setSelectedProvince(province);
    setSelectedPod(null);
  }, []);

  const handleRadiusChange = useCallback((radius) => {
    setSelectedRadius(radius);
  }, []);

  const handleBrandToggle = useCallback((brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  }, []);

  const handleSelectPod = useCallback((pod) => {
    setSelectedPod(pod);
    if (window.innerWidth < 1024) setSidebarOpen(false);
  }, []);

  const handleRefreshOutlets = useCallback(() => {
    localStorage.removeItem(ALL_OUTLETS_KEY);
    clearCache();
    hasLoadedRef.current = false;
    setAllOutlets([]);
    window.location.reload();
  }, []);

  // Select first POD on load
  useEffect(() => {
    if (filteredPods.length > 0 && !selectedPod) {
      setSelectedPod(filteredPods[0]);
    }
  }, [filteredPods, selectedPod]);

  // Loading state
  if (podsLoading) {
    return <LoadingPage message="Loading PODS data..." />;
  }

  // Error state
  if (podsError) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-dark-900 mb-2">Failed to Load Data</h2>
          <p className="text-dark-500">{podsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-dark-50 flex flex-col overflow-hidden">
      {/* Header */}
      <Header
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        className="flex-shrink-0"
      />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          pods={pods}
          selectedProvince={selectedProvince}
          onProvinceChange={handleProvinceChange}
          selectedRadius={selectedRadius}
          onRadiusChange={handleRadiusChange}
          selectedBrands={selectedBrands}
          onBrandToggle={handleBrandToggle}
          stats={stats}
          className="flex-shrink-0"
        />

        <main className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Map section */}
          <div className="flex-1 p-4 min-h-[300px] lg:min-h-0 relative">
            {outletsLoading && (
              <div className="absolute inset-4 z-20 bg-white/90 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-dark-700 font-medium">{loadProgress || 'Loading outlets...'}</p>
                </div>
              </div>
            )}
            <MapView
              pods={filteredPods}
              allPods={pods}
              outlets={mapOutlets}
              searchedOutlets={searchedOutlets}
              selectedPod={selectedPod}
              onSelectPod={handleSelectPod}
              selectedRadius={selectedRadius}
              selectedProvince={selectedProvince}
              showOutlets={true}
              showProximityRadius={!!selectedPod}
              className="h-full"
            />
          </div>

          {/* Details panel */}
          <div className="w-full lg:w-96 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-dark-200 bg-white overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* View toggle (mobile) */}
              <div className="flex gap-2 lg:hidden">
                <button
                  onClick={() => setViewMode('map')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    viewMode === 'map' ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'
                  }`}
                >
                  Map
                </button>
                <button
                  onClick={() => setViewMode('stats')}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                    viewMode === 'stats' ? 'bg-primary-500 text-white' : 'bg-dark-100 text-dark-600'
                  }`}
                >
                  Statistics
                </button>
              </div>

              {/* Status */}
              {allOutlets.length > 0 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center">
                  <p className="text-sm text-green-800">
                    <strong>{allOutlets.length}</strong> outlets • <strong>{filteredOutlets.length}</strong> in view
                  </p>
                  <button onClick={handleRefreshOutlets} className="text-xs text-green-600 hover:text-green-800 underline">
                    Refresh
                  </button>
                </div>
              )}

              {backgroundLoading && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">{loadProgress}</p>
                </div>
              )}

              <PodDetails pod={selectedPod} outlets={filteredOutlets} onClose={() => setSelectedPod(null)} />
              <ProximityStats pod={selectedPod} outlets={filteredOutlets} />

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                <OutletProximityChart outlets={filteredOutlets} />
                <CoverageDonut outlets={filteredOutlets} />
              </div>

              <OutletList outlets={filteredOutlets} selectedPod={selectedPod} maxDistance={selectedRadius} />

              {!isGooglePlacesConfigured() && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Setup Required:</strong> Add Google Places API key to <code className="bg-yellow-100 px-1 rounded">.env</code>
                  </p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
